import axios from 'axios';
import { AUTH_TOKEN_KEY } from './tokenService';

// Debug log all environment variables
console.log('Environment Variables:', {
  API_URL: process.env.REACT_APP_API_URL,
  NODE_ENV: process.env.NODE_ENV
});

const API_URL = process.env.REACT_APP_API_URL;

if (!API_URL) {
  console.error('API_URL environment variable is not set');
  throw new Error('API URL is not configured. Please check your .env file.');
}

// Create API instance
const api = axios.create({
  baseURL: API_URL.replace(/\/$/, ''), // Remove trailing slash if present
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true // Enable sending cookies in cross-origin requests
});

// Request interceptor to add auth token to all requests
api.interceptors.request.use(
  (config) => {
    console.group('🔄 [API] REQUEST INTERCEPTOR START');
    console.log(`🔄 [API] Preparing ${config.method?.toUpperCase()} request to ${config.url}`);
    
    // Get token directly from localStorage for consistency
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    
    console.log('🔄 [API] Token from localStorage:', {
      exists: !!token,
      length: token?.length,
      preview: token ? `${token.substring(0, 10)}...` : 'null'
    });
    
    if (token) {
      console.log('🔄 [API] Adding token to request headers');
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('🔄 [API] No token found for request');
    }
    
    console.log('🔄 [API] Final request headers:', {
      hasAuth: !!config.headers.Authorization,
      authHeader: config.headers.Authorization ? 'Bearer [REDACTED]' : 'none'
    });
    
    console.groupEnd();
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => {
    console.log(`✅ Response from ${response.config.url}: ${response.status}`);
    return response;
  },
  (error) => {
    console.error(`❌ API Error:`, {
      url: error.config?.url,
      status: error.response?.status,
      message: error.message
    });
    
    // Handle authentication errors
    if (error.response?.status === 401) {
      console.error('Authentication error - redirecting to login');
      
      // Clear invalid token
      localStorage.removeItem(AUTH_TOKEN_KEY);
      sessionStorage.removeItem(AUTH_TOKEN_KEY);
      
      // Redirect to login page
      window.location.href = '/';
    }
    
    return Promise.reject(error);
  }
);

export interface Query {
  query_id: string;
  user_id: string;
  created_at: string;
  topic: {
    main: string;
    persona: string;
    focus_areas: string[];
    exclude_topics: string[];
    custom_instructions: string;
  };
  schedule: {
    time_of_day: string;
    timezone: string;
  };
  metadata: {
    status: string;
    created_at: string;
    updated_at: string;
    last_run_time: string | null;
    next_run_time: string | null;
  };
  stats: {
    total_runs: number;
    total_success: number;
    total_errors: number;
  };
}

export interface User {
  user_id: string;
  email: string;
  /** 
   * Array of user queries - should always be an array, even if empty.
   * The API might return undefined/null, but we normalize this to an empty array.
   */
  queries: Query[];
}

/**
 * Check if a user exists and create one if they don't
 * @param email User's email address
 * @returns The user object
 */
export const initializeUser = async (email: string): Promise<User> => {
  try {
    console.log('🔍 Initializing user with email:', email);
    
    // First try to get the current user
    try {
      const userData = await getUser();
      console.log('✅ User already exists:', userData);
      return userData;
    } catch (error: any) {
      // If the error is not a 404 (user not found), rethrow it
      if (error.response?.status !== 404) {
        console.error('❌ Error checking user existence:', error);
        throw error;
      }
      
      // User doesn't exist, create a new one
      console.log('🆕 User not found, creating new user');
      return await createUser(email);
    }
  } catch (error) {
    console.error('❌ Failed to initialize user:', error);
    throw error;
  }
};

/**
 * Create a new user
 * @param email User's email address
 * @returns The created user object
 */
export const createUser = async (email: string): Promise<User> => {
  try {
    console.log('🆕 Creating user:', { email });
    
    const userData = {
      email,
      created_at: new Date().toISOString()
    };
    
    const response = await api.post('/users', userData);
    console.log('✅ User created successfully:', response.data);
    
    // Initialize with empty queries array if not present
    if (!response.data.queries) {
      response.data.queries = [];
    }
    
    return response.data;
  } catch (error: any) {
    console.error('❌ Error creating user:', {
      status: error.response?.status,
      data: error.response?.data,
      headers: error.response?.headers
    });
    throw error;
  }
};

/**
 * Get the current authenticated user
 * @returns The user object with queries
 */
export const getUser = async (): Promise<User> => {
  try {
    console.log('🔍 getUser API CALL ==================');
    
    // Check token directly
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    
    console.log('🔍 Token before API call:', {
      exists: !!token,
      length: token?.length || 0,
      preview: token ? `${token.substring(0, 10)}...` : 'null'
    });
    
    // Make the request - token will be added by interceptor
    console.log('🔍 Making API request to /users/me');
    const response = await api.get('/users/me');
    
    console.log('🔍 API response received:', response.status);
    
    // Ensure the response always has a queries array
    if (!response.data.queries) {
      console.log('🔍 No queries found in response, initializing empty array');
      response.data.queries = [];
    }
    
    // Return the response data with guaranteed queries array
    return response.data;
  } catch (error) {
    console.error('Error getting user:', error);
    throw error;
  }
};

export interface CreateQueryRequest {
  topic: {
    main: string;
    persona: string;
    focus_areas: string[];
    exclude_topics: string[];
    custom_instructions: string;
  };
  schedule: {
    time_of_day: string;
    timezone: string;
  };
}

/**
 * Create a new query for the current user
 * @param queryData Query data
 * @returns The created query
 */
export const createQuery = async (queryData: CreateQueryRequest): Promise<Query> => {
  try {
    console.log('📝 Creating new query:', queryData);
    
    // Send the query data directly to match the API schema
    const response = await api.post('/queries', queryData);
    console.log('✅ Query created successfully:', response.data);
    
    return response.data;
  } catch (error) {
    console.error('❌ Error creating query:', error);
    throw error;
  }
};

/**
 * Delete a query by ID
 * @param queryId Query ID to delete
 */
export const deleteQuery = async (queryId: string): Promise<void> => {
  try {
    console.log('🗑️ Deleting query:', queryId);
    
    // The backend expects the query_id in the URL path
    await api.delete(`/queries/${queryId}`);
    
    console.log('✅ Query deleted successfully');
  } catch (error: any) {
    console.error('❌ Error deleting query:', {
      queryId,
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });

    if (error.response?.status === 404) {
      throw new Error('Query not found.');
    } else if (error.response?.status === 401) {
      throw new Error('Please log in to delete queries.');
    } else {
      throw new Error('Failed to delete query. Please try again later.');
    }
  }
};

/**
 * Get dashboard data
 * @returns Dashboard data
 */
export const getDashboardData = async () => {
  try {
    const response = await api.get('/dashboard');
    return response.data;
  } catch (error) {
    console.error("API Error in getDashboardData:", error);
    throw error;
  }
};

// Add unsubscribe function
export const unsubscribe = async (queryId: string): Promise<void> => {
  try {
    console.log(`🔄 [API] Unsubscribing from query: ${queryId}`);
    
    // Use direct axios call without auth headers since this is a public endpoint
    const response = await axios({
      method: 'post',
      url: `${API_URL}/unsubscribe`,
      data: { query_id: queryId },
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    console.log(`✅ [API] Successfully unsubscribed from query: ${queryId}`, response.data);
    return response.data;
  } catch (error) {
    console.error(`❌ [API] Error unsubscribing from query: ${queryId}`, error);
    throw error;
  }
};

/**
 * Update query status (active/inactive)
 * @param queryId Query ID to update
 * @param status New status (active/inactive)
 * @returns Updated query
 */
export const updateQueryStatus = async (queryId: string, status: 'active' | 'inactive'): Promise<Query> => {
  try {
    console.log(`🔄 [API] Updating query status: ${queryId} to ${status}`);
    
    // Call the update query endpoint with just the metadata.status field
    const response = await api.put(`/queries/${queryId}`, {
      metadata: {
        status: status
      }
    });
    
    console.log(`✅ [API] Successfully updated query status: ${queryId}`, response.data);
    return response.data;
  } catch (error) {
    console.error(`❌ [API] Error updating query status: ${queryId}`, error);
    throw error;
  }
};

/**
 * Create a Stripe checkout session for subscription upgrade
 * @param planId The ID of the plan to subscribe to
 * @returns The URL to redirect to for checkout
 */
export const createCheckoutSession = async (planId: string): Promise<{ sessionUrl: string }> => {
  try {
    console.group('🔄 [API] Creating Stripe checkout session');
    console.log(`🔄 [API] Plan ID: ${planId}`);
    
    const response = await api.post('/subscription/create-checkout-session', { planId });
    
    console.log('🔄 [API] Checkout session created successfully:', {
      status: response.status,
      hasSessionUrl: !!response.data.sessionUrl
    });
    console.groupEnd();
    
    return response.data;
  } catch (error) {
    console.error('❌ [API] Error creating checkout session:', error);
    console.groupEnd();
    throw error;
  }
};

export default api; 